import { useEffect, useMemo, useRef, useState } from "react";
import {
  getAdminSettings,
  updateAdminSettings,
} from "../api/instateamAdminApi";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../api/instateamNotificationsApi";
import { AppIcon } from "../components/InstateamMasterComponents";
import {
  applyAdminTheme,
  formatDateTimeLabel,
  getInitials,
  normalizeHexColor,
  resolveAdminAccentColor,
} from "../utils/adminHelpers";

const SETTINGS_TABS = [
  { key: "theme", label: "Theme", icon: "palette" },
  { key: "notifications", label: "Notifications", icon: "bell" },
  { key: "workspace", label: "Workspace", icon: "settings" },
  { key: "account", label: "My Account", icon: "employees" },
];

const DEFAULT_SETTINGS = {
  theme: "warm",
  customAccentColor: "#ffc21a",
};

const DEFAULT_WORKSPACE = {
  companyName: "Insta Team",
  supportEmail: "support@instateam.com",
  supportPhone: "+91 98765 43210",
  officeLocation: "Ahmedabad, India",
  defaultShiftStart: "09:00",
  defaultShiftEnd: "18:00",
  weeklyOffDay: "Sunday",
  leavePolicyNote:
    "Plan leaves in advance and coordinate with your reporting manager for smooth team coverage.",
};

const DEFAULT_NOTIFICATION_SETTINGS = {
  channels: { inApp: true },
  preferences: {
    complaints: true,
    leave: true,
    tasks: true,
    announcements: true,
    payroll: true,
    attendance: true,
    profile: true,
    documents: true,
    chat: true,
    system: true,
  },
  quietHours: {
    enabled: true,
    startTime: "22:00",
    endTime: "07:00",
  },
};

const THEME_OPTIONS = [
  {
    key: "warm",
    label: "Warm Yellow",
    hint: "Default Insta Team admin accent",
    color: "#ffc21a",
  },
  {
    key: "emerald",
    label: "Emerald Green",
    hint: "Fresh, calm workspace accent",
    color: "#22c55e",
  },
  {
    key: "ocean",
    label: "Ocean Blue",
    hint: "Focused clean blue accent",
    color: "#3b82f6",
  },
  {
    key: "custom",
    label: "Custom",
    hint: "Pick your own portal accent color",
    color: "#f97316",
  },
];

const NOTIFICATION_OPTIONS = [
  ["tasks", "Tasks", "Task assignments, updates, and reminders.", "tasks"],
  ["leave", "Leaves", "Leave requests, approvals, and rejections.", "leaves"],
  ["attendance", "Attendance", "Attendance alerts and regularization reminders.", "attendance"],
  ["payroll", "Payroll", "Payroll runs, slips, and salary updates.", "payroll"],
  ["announcements", "Announcements", "Company-wide announcements and broadcasts.", "announcements"],
  ["chat", "Chat", "Direct message and group chat updates.", "chat"],
  ["documents", "Documents", "Document upload, verification, and access alerts.", "documents"],
  ["complaints", "Complaints", "Complaint status and response notifications.", "complaints"],
  ["profile", "Profile & Account", "Account activity and profile updates.", "employees"],
  ["system", "System", "Important platform-level notices.", "settings"],
];

const WEEKLY_OFF_OPTIONS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const buildAccountPayload = (source = {}) => ({
  fullName: source.fullName || "Admin User",
  email: source.email || "—",
  username: source.username || "—",
  roleLabel: source.roleLabel || "Admin",
  canManageAdminUsers: Boolean(source.canManageAdminUsers),
  lastLoginAt: source.lastLoginAt || null,
  createdAt: source.createdAt || null,
});

const buildNotificationState = (payload = {}) => ({
  channels: {
    inApp: true,
    ...(payload.channels || {}),
  },
  preferences: {
    ...DEFAULT_NOTIFICATION_SETTINGS.preferences,
    ...(payload.preferences || {}),
  },
  quietHours: {
    ...DEFAULT_NOTIFICATION_SETTINGS.quietHours,
    ...(payload.quietHours || {}),
  },
});

function SettingSwitch({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      className={`admin-settings-switch ${checked ? "active" : ""}`}
      onClick={() => !disabled && onChange?.(!checked)}
      disabled={disabled}
    >
      <span />
    </button>
  );
}

function SummaryStat({ label, value, icon }) {
  return (
    <div className="admin-settings-summary-stat">
      <div className="admin-settings-summary-icon">
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

export default function AdminSettingsPage({
  token,
  sessionAdmin,
  onSessionUpdate,
  onProtectedError,
  onNotify,
}) {
  const [activeTab, setActiveTab] = useState("theme");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);
  const [workspace, setWorkspace] = useState(DEFAULT_WORKSPACE);
  const [savedWorkspace, setSavedWorkspace] = useState(DEFAULT_WORKSPACE);
  const [account, setAccount] = useState(buildAccountPayload(sessionAdmin));
  const [notificationSettings, setNotificationSettings] = useState(
    DEFAULT_NOTIFICATION_SETTINGS
  );
  const [savedNotificationSettings, setSavedNotificationSettings] = useState(
    DEFAULT_NOTIFICATION_SETTINGS
  );
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingTheme, setIsSavingTheme] = useState(false);
  const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const persistedSettingsRef = useRef(savedSettings);

  useEffect(() => {
    setAccount(buildAccountPayload(sessionAdmin));
  }, [sessionAdmin]);

  useEffect(() => {
    persistedSettingsRef.current = savedSettings;
  }, [savedSettings]);

  useEffect(() => {
    if (!token) return undefined;
    let isActive = true;

    async function loadSettingsPage() {
      try {
        setIsLoading(true);
        const [settingsPayload, notificationsPayload] = await Promise.all([
          getAdminSettings(token),
          getNotificationSettings(token),
        ]);

        if (!isActive) return;

        const nextSettings = {
          ...DEFAULT_SETTINGS,
          ...(settingsPayload.settings || {}),
        };

        if (!normalizeHexColor(nextSettings.customAccentColor)) {
          nextSettings.customAccentColor = DEFAULT_SETTINGS.customAccentColor;
        }

        const nextWorkspace = {
          ...DEFAULT_WORKSPACE,
          ...(settingsPayload.workspace || {}),
        };

        const nextAccount = buildAccountPayload({
          ...sessionAdmin,
          ...(settingsPayload.account || {}),
        });

        const nextNotificationSettings = buildNotificationState(
          notificationsPayload.preferences
        );

        setSettings(nextSettings);
        setSavedSettings(nextSettings);
        setWorkspace(nextWorkspace);
        setSavedWorkspace(nextWorkspace);
        setAccount(nextAccount);
        setNotificationSettings(nextNotificationSettings);
        setSavedNotificationSettings(nextNotificationSettings);
        setMessage(null);
      } catch (error) {
        if (!isActive) return;

        setMessage({ type: "error", text: error.message });
        onNotify?.({ type: "error", text: error.message });
        onProtectedError?.(error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    loadSettingsPage();

    return () => {
      isActive = false;
    };
  }, [onNotify, onProtectedError, sessionAdmin, token]);

  useEffect(() => {
    applyAdminTheme(settings);
  }, [settings]);

  useEffect(
    () => () => {
      applyAdminTheme(persistedSettingsRef.current || DEFAULT_SETTINGS);
    },
    []
  );

  const hasThemeChanges = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(savedSettings),
    [savedSettings, settings]
  );

  const hasWorkspaceChanges = useMemo(
    () => JSON.stringify(workspace) !== JSON.stringify(savedWorkspace),
    [savedWorkspace, workspace]
  );

  const hasNotificationChanges = useMemo(
    () =>
      JSON.stringify(notificationSettings) !==
      JSON.stringify(savedNotificationSettings),
    [notificationSettings, savedNotificationSettings]
  );

  const previewAccent = resolveAdminAccentColor(settings);

  async function saveTheme() {
    if (settings.theme === "custom" && !normalizeHexColor(settings.customAccentColor)) {
      const warningMessage = "Pick a valid 6-digit custom accent color first.";
      setMessage({ type: "warning", text: warningMessage });
      onNotify?.({ type: "warning", text: warningMessage });
      return;
    }

    try {
      setIsSavingTheme(true);
      setMessage(null);

      const payload = await updateAdminSettings(token, {
        settings,
      });

      const nextSettings = {
        ...DEFAULT_SETTINGS,
        ...(payload.settings || {}),
      };

      setSettings(nextSettings);
      setSavedSettings(nextSettings);
      setAccount(buildAccountPayload(payload.account || sessionAdmin));
      onSessionUpdate?.(payload.admin);
      setMessage({ type: "success", text: payload.message || "Theme saved successfully." });
      onNotify?.({
        type: "success",
        text: payload.message || "Theme saved successfully.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    } finally {
      setIsSavingTheme(false);
    }
  }

  async function saveWorkspace() {
    try {
      setIsSavingWorkspace(true);
      setMessage(null);

      const payload = await updateAdminSettings(token, {
        workspace,
      });

      const nextWorkspace = {
        ...DEFAULT_WORKSPACE,
        ...(payload.workspace || {}),
      };

      setWorkspace(nextWorkspace);
      setSavedWorkspace(nextWorkspace);
      setAccount(buildAccountPayload(payload.account || sessionAdmin));
      if (payload.admin) {
        onSessionUpdate?.(payload.admin);
      }
      setMessage({
        type: "success",
        text: payload.message || "Workspace settings saved successfully.",
      });
      onNotify?.({
        type: "success",
        text: payload.message || "Workspace settings saved successfully.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function saveNotifications() {
    try {
      setIsSavingNotifications(true);
      setMessage(null);

      const payload = await updateNotificationSettings(token, notificationSettings);
      const nextNotificationSettings = buildNotificationState(payload.preferences);

      setNotificationSettings(nextNotificationSettings);
      setSavedNotificationSettings(nextNotificationSettings);
      setMessage({
        type: "success",
        text: payload.message || "Notification settings saved successfully.",
      });
      onNotify?.({
        type: "success",
        text: payload.message || "Notification settings saved successfully.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    } finally {
      setIsSavingNotifications(false);
    }
  }

  function updateThemeSetting(key, value) {
    setSettings((current) => ({ ...current, [key]: value }));
  }

  function updateWorkspaceField(key, value) {
    setWorkspace((current) => ({ ...current, [key]: value }));
  }

  function updateNotificationPreference(key, value) {
    setNotificationSettings((current) => ({
      ...current,
      preferences: {
        ...current.preferences,
        [key]: value,
      },
    }));
  }

  return (
    <section className="admin-settings-page">
      <article className="admin-settings-hero">
        <div className="admin-settings-hero-copy">
          <div className="admin-settings-hero-icon">
            <AppIcon name="settings" />
          </div>
          <div>
            <p className="admin-settings-kicker">Admin Settings</p>
            <h1>Personalize your admin workspace.</h1>
            <p>
              Manage your portal accent theme, notification flow, and shared
              workspace defaults from one compact place.
            </p>
          </div>
        </div>

        <div className="admin-settings-hero-side">
          <div className="admin-settings-profile-tile">
            <div className="admin-settings-avatar">{getInitials(account.fullName)}</div>
            <div>
              <strong>{account.fullName}</strong>
              <span>{account.roleLabel}</span>
              <small>{account.email}</small>
            </div>
          </div>
          <div className="admin-settings-color-pill">
            <span>Live Accent</span>
            <strong>{previewAccent.toUpperCase()}</strong>
          </div>
        </div>
      </article>

      <div className="admin-settings-tab-row">
        {SETTINGS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`admin-settings-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <AppIcon name={tab.icon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {message ? <div className={`status-banner ${message.type}`}>{message.text}</div> : null}

      {activeTab === "theme" ? (
        <div className="admin-settings-layout">
          <div className="admin-settings-stack">
            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Portal Theme</h2>
                  <p>Change the yellow accent across your admin portal.</p>
                </div>
                <span className="admin-settings-chip">Live Preview</span>
              </div>

              <div className="admin-theme-grid">
                {THEME_OPTIONS.map((option) => {
                  const isActive = settings.theme === option.key;
                  const accentColor =
                    option.key === "custom"
                      ? normalizeHexColor(settings.customAccentColor) || option.color
                      : option.color;

                  return (
                    <button
                      key={option.key}
                      type="button"
                      className={`admin-theme-card ${isActive ? "active" : ""}`}
                      onClick={() => updateThemeSetting("theme", option.key)}
                    >
                      <span
                        className="admin-theme-card-swatch"
                        style={{ background: accentColor }}
                      />
                      <strong>{option.label}</strong>
                      <small>{option.hint}</small>
                      {isActive ? (
                        <span className="admin-theme-card-check">
                          <AppIcon name="check" />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {settings.theme === "custom" ? (
                <div className="admin-settings-inline-grid">
                  <label className="form-field">
                    <span>Custom Accent Color</span>
                    <div className="field-shell">
                      <input
                        type="color"
                        value={
                          normalizeHexColor(settings.customAccentColor) ||
                          DEFAULT_SETTINGS.customAccentColor
                        }
                        onChange={(event) =>
                          updateThemeSetting("customAccentColor", event.target.value)
                        }
                      />
                    </div>
                  </label>

                  <label className="form-field">
                    <span>Hex Color</span>
                    <div className="field-shell">
                      <AppIcon name="palette" />
                      <input
                        type="text"
                        value={settings.customAccentColor}
                        onChange={(event) =>
                          updateThemeSetting(
                            "customAccentColor",
                            normalizeHexColor(event.target.value) || event.target.value
                          )
                        }
                        placeholder="#ffc21a"
                      />
                    </div>
                  </label>
                </div>
              ) : null}
            </article>

            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Theme Actions</h2>
                  <p>Save the selected accent for this admin account.</p>
                </div>
              </div>

              <div className="admin-settings-actions">
                <button
                  className="notification-outline-button"
                  type="button"
                  onClick={() => setSettings(savedSettings)}
                  disabled={!hasThemeChanges || isSavingTheme}
                >
                  Reset
                </button>
                <button
                  className="notification-primary-button"
                  type="button"
                  onClick={saveTheme}
                  disabled={isSavingTheme || !hasThemeChanges}
                >
                  <AppIcon name="check" />
                  {isSavingTheme ? "Saving..." : "Save Theme"}
                </button>
              </div>
            </article>
          </div>

          <aside className="admin-settings-side">
            <article className="admin-settings-card preview">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Accent Preview</h2>
                  <p>See how the current color flows through the admin UI.</p>
                </div>
              </div>

              <div className="admin-theme-preview-shell">
                <div
                  className="admin-theme-preview-banner"
                  style={{
                    background: `linear-gradient(135deg, ${previewAccent}, ${previewAccent}cc)`,
                  }}
                >
                  <strong>{account.roleLabel}</strong>
                  <span>{account.fullName}</span>
                </div>
                <div className="admin-theme-preview-panels">
                  <div className="admin-theme-preview-card">
                    <span className="dot" style={{ background: previewAccent }} />
                    Active tabs and highlight states
                  </div>
                  <div className="admin-theme-preview-card soft">
                    <AppIcon name="palette" />
                    Soft cards, chips, and progress accents
                  </div>
                  <div className="admin-theme-preview-button">Primary Save Action</div>
                </div>
              </div>
            </article>
          </aside>
        </div>
      ) : null}

      {activeTab === "notifications" ? (
        <div className="admin-settings-layout">
          <div className="admin-settings-stack">
            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Current Admin</h2>
                  <p>These notification preferences apply only to your logged-in account.</p>
                </div>
              </div>

              <div className="admin-settings-admin-strip">
                <div className="admin-settings-avatar medium">{getInitials(account.fullName)}</div>
                <div className="admin-settings-admin-strip-copy">
                  <strong>{account.fullName}</strong>
                  <span>{account.roleLabel}</span>
                  <small>{account.email}</small>
                </div>
                <div className="admin-settings-admin-strip-meta">
                  <span>Last Login</span>
                  <strong>{formatDateTimeLabel(account.lastLoginAt)}</strong>
                </div>
              </div>
            </article>

            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Notification Preferences</h2>
                  <p>Choose which admin alerts should reach you in-app.</p>
                </div>
                <label className="notification-select-all">
                  Select All
                  <input
                    type="checkbox"
                    checked={NOTIFICATION_OPTIONS.every(
                      ([key]) => notificationSettings.preferences?.[key] !== false
                    )}
                    onChange={(event) =>
                      setNotificationSettings((current) => ({
                        ...current,
                        preferences: Object.fromEntries(
                          NOTIFICATION_OPTIONS.map(([key]) => [key, event.target.checked])
                        ),
                      }))
                    }
                  />
                </label>
              </div>

              <div className="admin-settings-toggle-list">
                {NOTIFICATION_OPTIONS.map(([key, title, text, icon]) => (
                  <div className="notification-setting-row" key={key}>
                    <div className={`notification-icon ${icon}`}>
                      <AppIcon name={icon} />
                    </div>
                    <div>
                      <h3>{title}</h3>
                      <p>{text}</p>
                    </div>
                    <SettingSwitch
                      checked={notificationSettings.preferences?.[key] !== false}
                      onChange={(checked) => updateNotificationPreference(key, checked)}
                    />
                  </div>
                ))}
              </div>
            </article>
          </div>

          <aside className="admin-settings-side">
            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Quiet Hours</h2>
                  <p>Pause non-urgent admin alerts during your off-hours.</p>
                </div>
                <SettingSwitch
                  checked={notificationSettings.quietHours?.enabled}
                  onChange={(checked) =>
                    setNotificationSettings((current) => ({
                      ...current,
                      quietHours: {
                        ...current.quietHours,
                        enabled: checked,
                      },
                    }))
                  }
                />
              </div>

              <div className="admin-settings-inline-grid">
                <label className="form-field">
                  <span>Start Time</span>
                  <div className="field-shell">
                    <input
                      type="time"
                      value={notificationSettings.quietHours?.startTime || "22:00"}
                      onChange={(event) =>
                        setNotificationSettings((current) => ({
                          ...current,
                          quietHours: {
                            ...current.quietHours,
                            startTime: event.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>End Time</span>
                  <div className="field-shell">
                    <input
                      type="time"
                      value={notificationSettings.quietHours?.endTime || "07:00"}
                      onChange={(event) =>
                        setNotificationSettings((current) => ({
                          ...current,
                          quietHours: {
                            ...current.quietHours,
                            endTime: event.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                </label>
              </div>

              <div className="admin-settings-actions">
                <button
                  className="notification-outline-button"
                  type="button"
                  onClick={() => setNotificationSettings(savedNotificationSettings)}
                  disabled={!hasNotificationChanges || isSavingNotifications}
                >
                  Reset
                </button>
                <button
                  className="notification-primary-button"
                  type="button"
                  onClick={saveNotifications}
                  disabled={isSavingNotifications || !hasNotificationChanges}
                >
                  <AppIcon name="check" />
                  {isSavingNotifications ? "Saving..." : "Save Notifications"}
                </button>
              </div>
            </article>
          </aside>
        </div>
      ) : null}

      {activeTab === "workspace" ? (
        <div className="admin-settings-layout">
          <div className="admin-settings-stack">
            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Workspace Defaults</h2>
                  <p>Shared company details and default shift guidance for the portal.</p>
                </div>
              </div>

              <div className="admin-settings-form-grid">
                <label className="form-field">
                  <span>Company Name</span>
                  <div className="field-shell">
                    <AppIcon name="dashboard" />
                    <input
                      type="text"
                      value={workspace.companyName}
                      onChange={(event) =>
                        updateWorkspaceField("companyName", event.target.value)
                      }
                      placeholder="Enter company name"
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Support Email</span>
                  <div className="field-shell">
                    <AppIcon name="mail" />
                    <input
                      type="email"
                      value={workspace.supportEmail}
                      onChange={(event) =>
                        updateWorkspaceField("supportEmail", event.target.value)
                      }
                      placeholder="support@instateam.com"
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Support Phone</span>
                  <div className="field-shell">
                    <AppIcon name="support" />
                    <input
                      type="text"
                      value={workspace.supportPhone}
                      onChange={(event) =>
                        updateWorkspaceField("supportPhone", event.target.value)
                      }
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Office Location</span>
                  <div className="field-shell">
                    <AppIcon name="calendar" />
                    <input
                      type="text"
                      value={workspace.officeLocation}
                      onChange={(event) =>
                        updateWorkspaceField("officeLocation", event.target.value)
                      }
                      placeholder="Ahmedabad, India"
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Default Shift Start</span>
                  <div className="field-shell">
                    <input
                      type="time"
                      value={workspace.defaultShiftStart}
                      onChange={(event) =>
                        updateWorkspaceField("defaultShiftStart", event.target.value)
                      }
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Default Shift End</span>
                  <div className="field-shell">
                    <input
                      type="time"
                      value={workspace.defaultShiftEnd}
                      onChange={(event) =>
                        updateWorkspaceField("defaultShiftEnd", event.target.value)
                      }
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Weekly Off Day</span>
                  <div className="field-shell select-shell">
                    <select
                      value={workspace.weeklyOffDay}
                      onChange={(event) =>
                        updateWorkspaceField("weeklyOffDay", event.target.value)
                      }
                    >
                      {WEEKLY_OFF_OPTIONS.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                    <AppIcon name="chevronDown" />
                  </div>
                </label>
              </div>

              <label className="form-field">
                <span>Leave Policy Note</span>
                <textarea
                  className="admin-settings-textarea"
                  value={workspace.leavePolicyNote}
                  onChange={(event) =>
                    updateWorkspaceField("leavePolicyNote", event.target.value)
                  }
                  placeholder="Add a short leave policy note for your admin workspace"
                />
              </label>

              <div className="admin-settings-actions">
                <button
                  className="notification-outline-button"
                  type="button"
                  onClick={() => setWorkspace(savedWorkspace)}
                  disabled={!hasWorkspaceChanges || isSavingWorkspace}
                >
                  Reset
                </button>
                <button
                  className="notification-primary-button"
                  type="button"
                  onClick={saveWorkspace}
                  disabled={isSavingWorkspace || !hasWorkspaceChanges}
                >
                  <AppIcon name="check" />
                  {isSavingWorkspace ? "Saving..." : "Save Workspace"}
                </button>
              </div>
            </article>
          </div>

          <aside className="admin-settings-side">
            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Workspace Snapshot</h2>
                  <p>Quick view of the shared defaults currently configured.</p>
                </div>
              </div>

              <div className="admin-settings-summary-list">
                <SummaryStat
                  label="Company"
                  value={workspace.companyName}
                  icon="dashboard"
                />
                <SummaryStat
                  label="Default Shift"
                  value={`${workspace.defaultShiftStart} - ${workspace.defaultShiftEnd}`}
                  icon="clock"
                />
                <SummaryStat
                  label="Weekly Off"
                  value={workspace.weeklyOffDay}
                  icon="calendar"
                />
                <SummaryStat
                  label="Support Email"
                  value={workspace.supportEmail}
                  icon="mail"
                />
              </div>
            </article>
          </aside>
        </div>
      ) : null}

      {activeTab === "account" ? (
        <div className="admin-settings-layout">
          <div className="admin-settings-stack">
            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Current Logged-in Admin</h2>
                  <p>Read-only account information for the active admin session.</p>
                </div>
                {isLoading ? <span className="admin-settings-chip">Refreshing...</span> : null}
              </div>

              <div className="admin-settings-account-hero">
                <div className="admin-settings-avatar large">{getInitials(account.fullName)}</div>
                <div>
                  <strong>{account.fullName}</strong>
                  <span>{account.roleLabel}</span>
                  <small>{account.email}</small>
                </div>
              </div>

              <div className="admin-settings-account-grid">
                <div>
                  <span>Username</span>
                  <strong>{account.username}</strong>
                </div>
                <div>
                  <span>Access Type</span>
                  <strong>
                    {account.canManageAdminUsers ? "Super Admin Access" : "Admin Access"}
                  </strong>
                </div>
                <div>
                  <span>Last Login</span>
                  <strong>{formatDateTimeLabel(account.lastLoginAt)}</strong>
                </div>
                <div>
                  <span>Member Since</span>
                  <strong>{formatDateTimeLabel(account.createdAt)}</strong>
                </div>
              </div>
            </article>
          </div>

          <aside className="admin-settings-side">
            <article className="admin-settings-card">
              <div className="admin-settings-card-head">
                <div>
                  <h2>Security Note</h2>
                  <p>Password changes and admin user access remain in your profile panel.</p>
                </div>
              </div>

              <div className="admin-settings-note">
                <AppIcon name="shieldLock" />
                <div>
                  <strong>Secure account controls</strong>
                  <p>
                    Use the profile page to change your password and manage admin
                    users if you have super admin access.
                  </p>
                </div>
              </div>
            </article>
          </aside>
        </div>
      ) : null}
    </section>
  );
}
